import { useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  httpBatchLink,
  httpSubscriptionLink,
  loggerLink,
  retryLink,
  splitLink,
} from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { useAuth } from '@workos-inc/authkit-react';
import { EventSourcePolyfill, EventSourcePolyfillInit } from 'event-source-polyfill';
import superjson from 'superjson';
import { BackendRouter } from '~api';
import { joinPaths } from '~lib';
import { getAppEnv } from '~portal/env';
import { logger } from '~portal/lib';

// Retry configuration for SSE subscriptions
// Set to 1 for now (no retries), can be increased later
const SSE_MAX_RETRY_ATTEMPTS = 1;
const SSE_INITIAL_RETRY_DELAY_MS = 1000;
const SSE_MAX_RETRY_DELAY_MS = 30000;

/**
 * @desc Custom EventSource wrapper that logs SSE events and controls automatic reconnection.
 * EventSourcePolyfill has built-in auto-retry (1s delay) that can't be configured via options.
 * This wrapper closes the connection on error to prevent EventSource's retry, allowing
 * tRPC retryLink to control retry logic with exponential backoff and max attempts.
 */
class ControlledRetryEventSource extends EventSourcePolyfill {
  private hasErrored = false;

  constructor(url: string | URL, eventSourceInitDict?: EventSourcePolyfillInit) {
    const urlString = typeof url === 'string' ? url : url.toString();
    super(urlString, eventSourceInitDict);

    // Log connection establishment
    logger.debug('[SSE] EventSource connecting', { url: urlString });

    // Log all message events (includes pings and data)
    this.addEventListener('message', (event) => {
      const messageEvent = event as MessageEvent;
      const data = messageEvent.data;
      if (data === ':ping') {
        logger.debug('[SSE] Received ping', { timestamp: new Date().toISOString() });
      } else {
        logger.debug('[SSE] Received message', {
          dataLength: data.length,
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Log connection open and reset error flag
    this.addEventListener('open', () => {
      this.hasErrored = false;
      logger.debug('[SSE] Connection opened', { timestamp: new Date().toISOString() });
    });

    // Log errors and close connection to prevent EventSource auto-retry
    this.addEventListener('error', (event) => {
      if (this.hasErrored) {
        // Already handled this error, ignore subsequent error events
        return;
      }

      this.hasErrored = true;

      logger.error('[SSE] Connection error', {
        readyState: this.readyState,
        timestamp: new Date().toISOString(),
        event,
      });

      // Close connection to prevent EventSource's built-in 1s retry
      // tRPC retryLink will handle retry logic with proper backoff
      if (this.readyState !== EventSourcePolyfill.CLOSED) {
        logger.debug('[SSE] Closing connection to delegate retry to tRPC retryLink');
        this.close();
      }
    });
  }
}

// Create tRPC React hooks
export const api = createTRPCReact<BackendRouter>();

export type TApiClient = ReturnType<typeof api.useUtils>;

export function ApiProvider({ children }: { children: React.ReactNode }) {
  const { getAccessToken } = useAuth();

  // The SDK swaps in a new getAccessToken once its client initialises (the
  // pre-init one always rejects), so the memoised tRPC client must resolve the
  // current function through a ref rather than capture the first render's.
  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  // Create query client once per app instance
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  // Create tRPC client once per app instance
  const env = getAppEnv();
  const url = joinPaths(env.backendApiUrl, '/internal');

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (opts) =>
            import.meta.env.DEV || (opts.direction === 'down' && opts.result instanceof Error),
          console: {
            /* eslint-disable @typescript-eslint/no-explicit-any -- tRPC logger types */
            log: (...args: any[]) => console.trace(...args),
            error: (...args: any[]) => console.error(...args),
            /* eslint-enable */
          },
        }),
        retryLink({
          retry(opts) {
            // Only apply retry logic to subscriptions
            if (opts.op.type !== 'subscription') {
              return false;
            }

            // Don't retry on authentication errors - user needs to re-login
            const status = opts.error?.data?.httpStatus;
            if (status === 401 || status === 403) {
              logger.warn('[tRPC] Auth error, not retrying subscription', { status });
              return false;
            }

            // Don't retry on client errors (4xx) except rate limits (429)
            if (status && status >= 400 && status < 500 && status !== 429) {
              logger.warn('[tRPC] Client error, not retrying subscription', { status });
              return false;
            }

            // Check max retry attempts
            if (opts.attempts >= SSE_MAX_RETRY_ATTEMPTS) {
              logger.warn('[tRPC] Max retry attempts exceeded for subscription', {
                maxAttempts: SSE_MAX_RETRY_ATTEMPTS,
              });
              return false;
            }

            // Retry on network errors and 5xx server errors
            logger.info('[tRPC] Retrying subscription', {
              attempt: opts.attempts + 1,
              maxAttempts: SSE_MAX_RETRY_ATTEMPTS,
            });
            return true;
          },
          retryDelayMs(attemptIndex) {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s
            // attemptIndex is 0-based: 0 = first retry, 1 = second retry, etc.
            const delay = Math.min(
              SSE_INITIAL_RETRY_DELAY_MS * Math.pow(2, attemptIndex),
              SSE_MAX_RETRY_DELAY_MS,
            );
            logger.debug('[tRPC] Retry delay calculated', {
              delay,
              attempt: attemptIndex + 1,
            });
            return delay;
          },
        }),
        splitLink({
          // uses the httpSubscriptionLink for subscriptions
          condition: (op) => op.type === 'subscription',
          true: httpSubscriptionLink({
            url,
            transformer: superjson,
            EventSource: ControlledRetryEventSource,
            eventSourceOptions: async () => {
              try {
                const token = await getAccessTokenRef.current();
                return {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  heartbeatTimeout: env.sse.heartbeatTimeoutSeconds * 1_000,
                };
              } catch {
                return {};
              }
            },
          }),
          false: httpBatchLink({
            url,
            transformer: superjson,
            async headers() {
              try {
                const token = await getAccessTokenRef.current();
                return {
                  Authorization: `Bearer ${token}`,
                };
              } catch {
                return {};
              }
            },
          }),
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </api.Provider>
  );
}
